/**
 * Live Training mode for a training block: how the coach runs it and which
 * tools the coach dashboard offers. Derived from the catalog exercise type and
 * the block's execution type (sequences / once / rounds).
 *
 *   kot          King of the Table: rounds, matches, leaderboard.
 *   sequences    Competency blocks, warmup, service: timed blocks, roles swap.
 *   multiball    One side performs while the partner (or coach) feeds; roles swap.
 *   match        Match play and challenges: per-table scoreboard.
 *   interval     Timer exercises (shadow, stretching, visualisation): work / rest rounds.
 *   off_table    Off-table circuits and timer-only blocks: chrono and count.
 *   table_drill  Technical drills at the table, both sides active, single timer.
 */

import {
  exerciseTypeNameCandidates,
  isKotExercise,
  isMultiballExercise,
  isOffTableExercise,
  isServiceExercise,
  isTeSequences,
} from "./teExecutionType";
import { coachToolsFromExercise, isLiveTimerBlock } from "./teCoachTools";

export const LIVE_EXERCISE_MODES = Object.freeze({
  KOT: "kot",
  SEQUENCES: "sequences",
  MULTIBALL: "multiball",
  MATCH: "match",
  INTERVAL: "interval",
  OFF_TABLE: "off_table",
  TABLE_DRILL: "table_drill",
});

function stripAccents(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function typeNames(ex) {
  return exerciseTypeNameCandidates(ex).map(stripAccents);
}

function anyName(ex, predicate) {
  return typeNames(ex).some(predicate);
}

export function isMatchTypeName(name) {
  const n = stripAccents(name);
  return n === "match" || n.startsWith("match ") || n === "partido" || n === "partida";
}

export function isChallengeTypeName(name) {
  const n = stripAccents(name);
  return (
    n.includes("challenge") ||
    n === "defi" ||
    n === "defis" ||
    n === "reto" ||
    n === "retos" ||
    n.includes("desafio") ||
    n.includes("herausforderung")
  );
}

/** Shadow training, stretching, visualisation: no ball, everyone works on the clock. */
export function isIntervalTypeName(name) {
  const n = stripAccents(name);
  return (
    n.includes("shadow") ||
    n.includes("fantome") ||
    n.includes("schatten") ||
    n.includes("sombra") ||
    n.includes("streach") ||
    n.includes("stretch") ||
    n.includes("etirement") ||
    n.includes("estiramiento") ||
    n.includes("dehn") ||
    n.includes("visualis") ||
    n.includes("visualiz")
  );
}

export function isRegularityTypeName(name) {
  const n = stripAccents(name);
  return n.includes("regularit") || n.includes("regelmassig");
}

function isStretchingTypeName(name) {
  const n = stripAccents(name);
  return (
    n.includes("streach") ||
    n.includes("stretch") ||
    n.includes("etirement") ||
    n.includes("estiramiento") ||
    n.includes("dehn")
  );
}

function isVisualisationTypeName(name) {
  const n = stripAccents(name);
  return n.includes("visualis") || n.includes("visualiz");
}

const INTERVAL_DEFAULTS = Object.freeze({
  shadow: { workSec: 30, restSec: 15, rounds: 6 },
  stretching: { workSec: 30, restSec: 10, rounds: 8 },
  visualisation: { workSec: 120, restSec: 0, rounds: 1 },
  default: { workSec: 40, restSec: 20, rounds: 5 },
});

function intervalVariant(ex) {
  if (anyName(ex, isStretchingTypeName)) return "stretching";
  if (anyName(ex, isVisualisationTypeName)) return "visualisation";
  if (anyName(ex, isIntervalTypeName)) return "shadow";
  return "default";
}

function blankTools() {
  return { chrono: false, count: false, interval: false, match: false };
}

/**
 * @param {object|null|undefined} ex training block (live / planning row)
 * @returns {{
 *   mode: string,
 *   variant: string,
 *   usesTables: boolean,
 *   activeSides: "both"|"one"|"rotating"|"none",
 *   tools: { chrono: boolean, count: boolean, interval: boolean, match: boolean },
 *   countLabelKey: string|null,
 *   defaultBestOf: number,
 *   intervalDefaults: { workSec: number, restSec: number, rounds: number },
 * }}
 */
export function resolveLiveExerciseMode(ex) {
  const base = {
    mode: LIVE_EXERCISE_MODES.TABLE_DRILL,
    variant: "default",
    usesTables: true,
    activeSides: "both",
    tools: blankTools(),
    countLabelKey: null,
    defaultBestOf: 3,
    intervalDefaults: INTERVAL_DEFAULTS.default,
  };
  if (!ex) return base;

  if (isKotExercise(ex)) {
    return { ...base, mode: LIVE_EXERCISE_MODES.KOT, variant: "kot" };
  }

  const timerOnly = isLiveTimerBlock(ex);
  if (timerOnly || isOffTableExercise(ex)) {
    const configured = coachToolsFromExercise(ex);
    return {
      ...base,
      mode: LIVE_EXERCISE_MODES.OFF_TABLE,
      variant: timerOnly ? "timer" : "off_table",
      usesTables: false,
      activeSides: "none",
      tools: {
        ...blankTools(),
        chrono: Boolean(configured.chrono),
        count: !timerOnly && Boolean(configured.count),
        interval: true,
      },
    };
  }

  if (anyName(ex, isIntervalTypeName)) {
    const variant = intervalVariant(ex);
    return {
      ...base,
      mode: LIVE_EXERCISE_MODES.INTERVAL,
      variant,
      usesTables: false,
      activeSides: "none",
      tools: { ...blankTools(), interval: true },
      intervalDefaults: INTERVAL_DEFAULTS[variant] || INTERVAL_DEFAULTS.default,
    };
  }

  if (isMultiballExercise(ex)) {
    return {
      ...base,
      mode: LIVE_EXERCISE_MODES.MULTIBALL,
      variant: "multiball",
      activeSides: "one",
      tools: { ...blankTools(), count: true },
      countLabelKey: "liveModeCountLabelMultiball",
    };
  }

  if (anyName(ex, isMatchTypeName) || anyName(ex, isChallengeTypeName)) {
    const challenge = !anyName(ex, isMatchTypeName);
    return {
      ...base,
      mode: LIVE_EXERCISE_MODES.MATCH,
      variant: challenge ? "challenge" : "match",
      tools: { ...blankTools(), match: true },
      defaultBestOf: challenge ? 1 : 3,
    };
  }

  if (isTeSequences(ex)) {
    const service = isServiceExercise(ex);
    return {
      ...base,
      mode: LIVE_EXERCISE_MODES.SEQUENCES,
      variant: service ? "service" : "sequences",
      activeSides: "rotating",
      tools: { ...blankTools(), count: service },
      countLabelKey: service ? "liveModeCountLabelService" : null,
    };
  }

  const regularity = anyName(ex, isRegularityTypeName);
  return {
    ...base,
    variant: regularity ? "regularity" : "default",
    tools: { ...blankTools(), count: true, interval: !regularity },
    countLabelKey: regularity ? "liveModeCountLabelStreak" : "liveModeCountLabelReps",
  };
}

/** i18n keys for the coach mode strip. */
export function liveExerciseModeCopyKeys(profile) {
  const variant = profile?.variant || "default";
  const mode = profile?.mode || LIVE_EXERCISE_MODES.TABLE_DRILL;
  return {
    titleKey: `liveMode_${mode}_title`,
    briefKey: `liveMode_${mode}_${variant}_brief`,
  };
}

/** Multiball relabels sparring as the feeder; other modes keep performer / sparring. */
export function liveModeRoleLabelOverride(profile, role, t) {
  if (profile?.mode !== LIVE_EXERCISE_MODES.MULTIBALL || !role) return null;
  const key = String(role).toLowerCase();
  if (key === "sparring" || key.includes("spar")) {
    return t("liveModeRoleFeeder", "Feeder");
  }
  if (key === "active" || key === "both" || key.includes("perform")) {
    return t("liveModeRolePerformer", "Performer");
  }
  return null;
}
